import React from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';


export const AssetDetails = ({ assetData, searchQuery }) => {
  // Use the searchQuery from main page instead of local state
  // const [searchTerm, setSearchTerm] = useState('');

  if (!assetData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">No asset data available</p>
      </div>
    );
  }

  const { assetsOverview, tableDetails } = assetData;

  // Function to check if a column matches the search term from main page
  const isColumnMatch = (column) => {
    if (!searchQuery || !searchQuery.trim()) return false; // Only highlight if there's a main search
    
    const searchLower = searchQuery.toLowerCase();
    // Convert search query to underscore format (e.g., "expiry date" -> "expiry_date")
    const searchWithUnderscores = searchLower.replace(/\s+/g, '_');
    
    const columnName = (column.name || '').toLowerCase();
    const columnType = (column.type || '').toLowerCase();
    const columnMetadata = (column.metadata || '').toLowerCase();
    const columnSample = (column.sample || '').toLowerCase();
    
    return columnName.includes(searchLower) || 
           columnName.includes(searchWithUnderscores) ||
           columnType.includes(searchLower) || 
           columnType.includes(searchWithUnderscores) ||
           columnMetadata.includes(searchLower) || 
           columnMetadata.includes(searchWithUnderscores) ||
           columnSample.includes(searchLower) ||
           columnSample.includes(searchWithUnderscores);
  };

  // Filter table details based on main search term
  const filteredTableDetails = tableDetails || []; // Show all columns, but highlight matching ones

  // Function to get background color for data types
  const getTypeBackgroundColor = (type) => {
    if (!type) return '#6B7280'; // Default gray
    
    const normalizedType = type.toLowerCase();
    
    // Date types - Red background
    if (normalizedType.includes('date') || normalizedType.includes('time') || normalizedType === 'timestamp') {
      return '#EF4444'; // Red
    }
    
    // String types - Light blue background (like in the image)
    if (normalizedType.includes('string') || normalizedType.includes('char') || normalizedType.includes('text') || normalizedType.includes('varchar')) {
      return '#60A5FA'; // Light blue (darker than before for better contrast)
    }
    
    // Numeric types - Green background
    if (normalizedType.includes('int') || normalizedType.includes('number') || normalizedType.includes('float') || normalizedType.includes('decimal') || normalizedType.includes('double')) {
      return '#10B981'; // Green
    }
    
    // Boolean types - Purple background
    if (normalizedType.includes('bool') || normalizedType.includes('boolean')) {
      return '#8B5CF6'; // Purple
    }
    
    // Default - Gray background
    return '#6B7280';
  };

  // Function to get text color for data types
  const getTypeTextColor = (type) => {
    if (!type) return '#FFFFFF';
    
    const normalizedType = type.toLowerCase();
    
    // All types should have white text for better contrast
    return '#FFFFFF';
  };

  return (
    <div className="space-y-6">
      {/* Asset Overview Section - Now Tabular */}
      <div>
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <Typography variant="h5" sx={{ 
              color: '#bb1ccc', 
              fontWeight: 700, 
              fontSize: '1.5rem',
              marginBottom: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Box
                sx={{
                  width: '4px',
                  height: '24px',
                  backgroundColor: '#bb1ccc',
                  borderRadius: '2px'
                }}
              />
              {assetsOverview?.data_asset_name || 'Asset Name'}
            </Typography>
            
            <Typography variant="body2" sx={{ 
              color: '#000000', 
              fontSize: '0.875rem',
              fontStyle: 'normal'
            }}>
              {assetsOverview?.data_assets_description || 'No description available'}
            </Typography>
          </div>
          
          {/* Asset Type Tag */}
          <Box
            sx={{
              backgroundColor: (() => {
                const type = assetsOverview?.data_asset_type?.toLowerCase();
                switch (type) {
                  case 'bronze': return '#CD7F32';
                  case 'silver': return '#C0C0C0';
                  case 'gold': return '#FFD700';
                  case 'ingestion': return '#4F46E5';
                  default: return '#6B7280';
                }
              })(),
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              minWidth: 'fit-content',
              textAlign: 'center'
            }}
          >
            {assetsOverview?.data_asset_type || 'N/A'}
          </Box>
        </div>
      </div>

      {/* Country, Region, Frequency, Row Count Cards - Now 4 boxes in responsive grid */}
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">



{/* 
<Box
  sx={{
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    padding: 3,
    border: '1px solid #e0e0e0',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-2px)'
    }
  }}
>
  <Typography
    variant="body2"
    sx={{
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: 1,
      textTransform: 'uppercase'
    }}
  >
    Country
  </Typography>

  <Tooltip 
    title={assetsOverview?.country || 'N/A'} 
    placement="top-start" 
    arrow
  >
    <Box>
      <Typography
        variant="h6"
        sx={{
          wordBreak: 'break-word',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,          // show up to 2 lines
          WebkitBoxOrient: 'vertical',
          fontWeight: 700,
          fontSize: '1.25rem',
          color: '#1f2937',
          maxWidth: '100%',
          cursor: 'pointer'
        }}
      >
        {((assetsOverview?.country || 'N/A')
          .split(' ')
          .slice(0, 60)
          .join(' '))}
      </Typography>
    </Box>
  </Tooltip>
</Box> */}
<Box
  sx={{
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    padding: 3,
    border: '1px solid #e0e0e0',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-2px)'
    }
  }}
>
  {/* Label */}
  <Typography
    variant="body2"
    sx={{
      fontWeight: 600,
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: 1,
      textTransform: 'uppercase'
    }}
  >
    Country
  </Typography>

  {/* ✅ Native tooltip (no MUI Tooltip needed) */}
  <Typography
    variant="h6"
    title={assetsOverview?.country || 'N/A'} // <-- native HTML tooltip
    sx={{
      wordBreak: 'break-word',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      fontWeight: 700,
      fontSize: '1.25rem',
      color: '#1f2937',
      maxWidth: '100%',
      cursor: 'pointer'
    }}
  >
    {((assetsOverview?.country || 'N/A')
      .split(' ')
      .slice(0, 60)
      .join(' '))}
  </Typography>
</Box>




          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              padding: 3,
              border: '1px solid #e0e0e0',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: 1,
                textTransform: 'uppercase'
              }}
            >
              Region
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.25rem',
                color: '#1f2937'
              }}
            >
              {assetsOverview?.region || 'N/A'}
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              padding: 3,
              border: '1px solid #e0e0e0',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: 1,
                textTransform: 'uppercase'
              }}
            >
              Frequency
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.25rem',
                color: '#1f2937'
              }}
            >
              {assetsOverview?.frequency || 'N/A'}
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              padding: 3,
              border: '1px solid #e0e0e0',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#6b7280',
                marginBottom: 1,
                textTransform: 'uppercase'
              }}
            >
              Row Count
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.25rem',
                color: '#1f2937'
              }}
            >
              {assetsOverview?.data_row_count || 'N/A'}
            </Typography>
          </Box>
        </div>
      </div>

      {/* Table Details Section */}
      <div>
        <div className="flex justify-between items-start mb-3">
          <Typography variant="h6" sx={{ 
            color: '#bb1ccc', 
            fontWeight: 600, 
            fontSize: '1.25rem',
            marginBottom: 1
          }}>
            Table Details
          </Typography>
        </div>

        <TableContainer component={Paper} sx={{
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', 
          border: '1px solid #e0e0e0', 
          borderRadius: '12px',
          maxHeight: '500px',
          overflow: 'auto'
        }}>
          <Table sx={{ minWidth: 650 }} stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#bb1ccc' }}>
                <TableCell sx={{ 
                  color: "white", 
                  fontWeight: 600, 
                  fontSize: '14px',
                  padding: '16px 20px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: '#bb1ccc !important'
                }}>
                  Column Name
                </TableCell>
                <TableCell sx={{ 
                  color: "white", 
                  fontWeight: 600, 
                  fontSize: '14px',
                  padding: '16px 20px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: '#bb1ccc !important',
                  textAlign: 'center'
                }}>
                  Type
                </TableCell>
                <TableCell sx={{ 
                  color: "white", 
                  fontWeight: 600, 
                  fontSize: '14px',
                  padding: '16px 20px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: '#bb1ccc !important'
                }}>
                  Description
                </TableCell>
                <TableCell sx={{ 
                  color: "white", 
                  fontWeight: 600, 
                  fontSize: '14px',
                  padding: '16px 20px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: '#bb1ccc !important'
                }}>
                  Sample Value
                </TableCell>
                {/* <TableCell sx={{ 
                  color: "white", 
                  fontWeight: 600, 
                  fontSize: '14px',
                  padding: '16px 20px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  backgroundColor: '#bb1ccc !important'
                }}>
                  Row Count
                </TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTableDetails?.length > 0 ? (
                filteredTableDetails.map((column, index) => (
                  <TableRow 
                    key={column.attribute_id}
                    sx={{ 
                      backgroundColor: isColumnMatch(column) && searchQuery ? '#fff3cd' : (index % 2 === 0 ? '#fafafa' : '#ffffff'),
                      '&:hover': {
                        backgroundColor: '#f0f8ff',
                        transition: 'background-color 0.2s ease'
                      },
                      // Highlight matching rows with a subtle border
                      ...(isColumnMatch(column) && searchQuery && {
                        border: '2px solid #ffc107',
                        boxShadow: '0 2px 8px rgba(255, 193, 7, 0.3)'
                      })
                    }}
                  >
                    <TableCell sx={{ 
                      padding: '16px 20px',
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#2c3e50'
                    }}>
                      {column.name}
                    </TableCell>
                    <TableCell sx={{ 
                      padding: '16px 20px',
                      fontSize: '14px',
                      color: '#555',
                      textTransform: 'capitalize'
                    }}>
                      {column.type}
                    </TableCell>
                    <TableCell sx={{ 
                      padding: '16px 20px',
                      fontSize: '14px',
                      color: '#555',
                      maxWidth: '300px',
                      wordWrap: 'break-word'
                    }}>
                      {column.metadata || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ 
                      padding: '16px 20px',
                      fontSize: '14px',
                      color: '#555',
                      maxWidth: '200px',
                      wordWrap: 'break-word'
                    }}>
                      {column.sample || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>
                    {searchQuery ? `No columns found matching "${searchQuery}"` : 'No table details available'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default AssetDetails; 